/**
 * Список розыгрышей с постраничной загрузкой: история игрока (с его результатом) или все
 * розыгрыши площадки (для администратора). Строка открывает повтор розыгрыша.
 * На узких экранах таблица превращается в карточки.
 */
import { useState } from 'react';
import type { GameListItem, Page } from '@mt/protocol';
import { useLoad } from '../../lib/api';
import { crn, pts } from '../../lib/format';
import { Link } from '../../lib/router';
import { dateTime, Empty, Pager, toneOf } from '../kit';

const KIND: Record<GameListItem['kind'], string> = { market: 'рынок', walkover: 'остался один', cancel: 'отмена' };

export function GameList({ load, replayPath, mode, pageSize = 20 }: { load: (offset: number, limit: number) => Promise<Page<GameListItem>>; replayPath: (id: string) => string; mode: 'player' | 'admin'; pageSize?: number }) {
  const [offset, setOffset] = useState(0);
  const { data, error, loading } = useLoad(() => load(offset, pageSize), [offset]);
  if (error) return <p className="notice danger">{error}</p>;
  if (!data) return <p className="muted-line">{loading ? 'Загрузка…' : ''}</p>;
  if (data.total === 0) return <Empty>{mode === 'player' ? 'Розыгрышей пока нет — сядьте за стол в лобби.' : 'Розыгрышей пока не было.'}</Empty>;
  return (
    <>
      <div className="table-scroll">
        <table className="data-table games-table">
          <thead>
            <tr>
              <th>Время</th>
              <th>Стол</th>
              <th>Состав</th>
              <th className="num">Δ</th>
              {mode === 'player' ? (
                <>
                  <th>Прогноз</th>
                  <th className="num">Внесено</th>
                  <th className="num">Итог</th>
                </>
              ) : (
                <>
                  <th className="num">Игроки</th>
                  <th className="num">Банк</th>
                  <th className="num">Комиссия</th>
                </>
              )}
              <th />
            </tr>
          </thead>
          <tbody>
            {data.items.map((g) => (
              <tr key={g.gameId} className={g.status}>
                <td data-label="Время" className="mono">
                  {dateTime(g.settledAt)}
                </td>
                <td data-label="Стол">
                  {g.ante / 100} кр.{g.mode === 'replay' && <span className="tag">история</span>}
                </td>
                <td data-label="Состав" className="mono small">
                  {g.composition.join(' · ') || '—'}
                </td>
                <td data-label="Δ" className={`num mono ${g.delta ? toneOf(g.delta) : ''}`}>
                  {g.status === 'cancelled' ? <span className="dim">{KIND[g.kind]}</span> : g.kind === 'walkover' ? <span className="dim">{KIND.walkover}</span> : pts(g.delta, true)}
                </td>
                {mode === 'player' && g.me ? (
                  <>
                    <td data-label="Прогноз">
                      {g.me.folded ? (
                        <span className="dim">пас</span>
                      ) : g.me.direction ? (
                        <span className={g.me.direction === 'up' ? 'up' : 'down'}>
                          {g.me.direction === 'up' ? '▲' : '▼'} {pts(g.me.points)}
                          {g.me.correct && g.me.error !== null && <span className="dim small"> · ошибка {pts(g.me.error)}</span>}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td data-label="Внесено" className="num mono">
                      {crn(g.me.contributed)}
                    </td>
                    <td data-label="Итог" className={`num mono ${toneOf(g.me.net)}`}>
                      {g.me.net > 0 ? '+' : ''}
                      {crn(g.me.net)}
                    </td>
                  </>
                ) : (
                  <>
                    <td data-label="Игроки" className="num mono">
                      {g.humans}/{g.players}
                    </td>
                    <td data-label="Банк" className="num mono">
                      {crn(g.pot)}
                    </td>
                    <td data-label="Комиссия" className="num mono">
                      {crn(g.commission)}
                    </td>
                  </>
                )}
                <td className="num row-action">
                  <Link className="btn tiny" to={replayPath(g.gameId)}>
                    Как шла игра
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager offset={data.offset} limit={data.limit} total={data.total} onPage={setOffset} />
    </>
  );
}
